/**
 * 8.2 — Progression individuelle : difficulté en semaine 1 contre semaine 4.
 *
 * C'est la seule analyse du lot qui suit des comptes UN PAR UN. La courbe
 * hebdomadaire de l'onglet Apprentissage montre une moyenne de population, qui
 * baisse dès qu'arrivent des débutants même si personne n'a régressé. Ici,
 * chaque compte est comparé à lui-même.
 *
 * Deux vues de la même donnée, et elles ne disent pas la même chose :
 *   · l'histogramme du delta répond « combien progressent ? » ;
 *   · le nuage S1 × S4 répond « qui progresse ? » — un débutant qui passe de 1
 *     à 2 et un joueur avancé qui passe de 4 à 5 ont le même delta mais rien à
 *     voir l'un avec l'autre.
 */

import {
  Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Scatter,
  ScatterChart, Tooltip, XAxis, YAxis, ZAxis,
} from 'recharts';

import { PROGRESSION_THRESHOLD } from '@shared/types/stats';

import { formatDecimal, formatInteger, formatPercent } from '../../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS, SEQUENTIAL_RAMP } from '../../lib/theme';
import type { DataColumn, LabelledBucket, ProgressionAnalysis } from '../../types';
import { ChartCard } from '../Card';
import { ChartTooltip } from '../ChartTooltip';
import { ReadingBox } from '../ReadingBox';
import { StatTile } from '../StatTile';

const histogramColumns: ReadonlyArray<DataColumn<LabelledBucket>> = [
  { key: 'label', header: 'Écart S4 − S1', render: (row) => row.label },
  { key: 'users', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.users) },
];

function verdict(data: ProgressionAnalysis): string {
  if (data.eligibleUsers === 0) {
    return "Aucun compte ne remplit les conditions sur cette période : il faut quatre semaines d'ancienneté et au moins trois apprentissages en semaine 1 comme en semaine 4.";
  }
  if (data.stableShare >= 50) {
    return `Une majorité stagne : ${formatPercent(data.stableShare)} des comptes jouent en semaine 4 une difficulté équivalente à celle de leur première semaine.`;
  }
  if (data.progressingShare > data.regressingShare * 1.5) {
    return `La progression domine : ${formatPercent(data.progressingShare)} des comptes montent en difficulté entre la semaine 1 et la semaine 4, contre ${formatPercent(data.regressingShare)} qui redescendent.`;
  }
  if (data.regressingShare > data.progressingShare) {
    return `Plus de comptes redescendent (${formatPercent(data.regressingShare)}) qu'ils ne montent (${formatPercent(data.progressingShare)}) — le catalogue les envoie peut-être trop haut trop tôt.`;
  }
  return `Progression et régression s'équilibrent : ${formatPercent(data.progressingShare)} contre ${formatPercent(data.regressingShare)}.`;
}

export function ProgressionPanel({ data }: { data: ProgressionAnalysis }) {
  return (
    <>
      <ReadingBox
        method={
          <>
            Un compte est retenu s'il a au moins quatre semaines d'ancienneté observée et au
            moins trois apprentissages en semaine 1 <em>et</em> en semaine 4 — une moyenne sur un
            seul morceau serait du bruit, et comparer deux bruits fabrique une fausse
            progression. Les semaines sont relatives à l'entrée du compte, pas au calendrier. Un
            écart inférieur à {formatDecimal(PROGRESSION_THRESHOLD)} point compte comme stable.
          </>
        }
      >
        {verdict(data)}
      </ReadingBox>

      <div className="kpi-row">
        <StatTile
          label="Comptes analysés"
          value={formatInteger(data.eligibleUsers)}
          hint="4 semaines d'ancienneté, 3 apprentissages par semaine"
        />
        <StatTile
          label="Écart moyen"
          value={data.avgDelta > 0 ? `+${formatDecimal(data.avgDelta)}` : formatDecimal(data.avgDelta)}
          hint={`médiane ${data.medianDelta > 0 ? '+' : ''}${formatDecimal(data.medianDelta)}`}
        />
        <StatTile label="Progressent" value={formatPercent(data.progressingShare)} hint="écart > +0,1" />
        <StatTile label="Stagnent" value={formatPercent(data.stableShare)} hint="écart entre −0,1 et +0,1" />
        <StatTile label="Régressent" value={formatPercent(data.regressingShare)} hint="écart < −0,1" />
      </div>

      <div className="chart-grid">
        <ChartCard
          title="Distribution de l'écart"
          subtitle="Difficulté moyenne en semaine 4 moins celle de la semaine 1"
          rows={data.deltaHistogram}
          columns={histogramColumns}
          chartHeight={280}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.deltaHistogram as LabelledBucket[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
                axisLine={{ stroke: COLORS.grid }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                axisLine={false}
                tickLine={false}
                width={44}
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                content={<ChartTooltip valueFormatter={formatInteger} />}
              />
              <Bar isAnimationActive={false} dataKey="users" name="Comptes" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE * 2}>
                {/* Seaux ordonnes de la regression a la progression : la rampe
                    redouble cet ordre. */}
                {data.deltaHistogram.map((bucket, index) => (
                  <Cell key={bucket.label} fill={SEQUENTIAL_RAMP[Math.min(index, SEQUENTIAL_RAMP.length - 1)]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Semaine 1 contre semaine 4"
          subtitle="Un point par compte — au-dessus de la diagonale, il a progressé"
          emptyMessage="Aucun compte éligible sur cette période."
          rows={[]}
          columns={[]}
          chartHeight={280}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke={COLORS.grid} />
              <XAxis
                type="number"
                dataKey="week1"
                name="Semaine 1"
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                axisLine={{ stroke: COLORS.grid }}
                tickLine={false}
              />
              <YAxis
                type="number"
                dataKey="week4"
                name="Semaine 4"
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <ZAxis range={[36, 36]} />
              <Tooltip
                cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
                content={<ChartTooltip valueFormatter={(value) => formatDecimal(value)} />}
              />
              {/* La diagonale y = x : c'est elle qui rend le nuage lisible.
                  Sans repere, l'oeil ne sait pas ou est la stagnation. */}
              <ReferenceLine
                segment={[{ x: 1, y: 1 }, { x: 5, y: 5 }]}
                stroke={COLORS.brandCyan}
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              <Scatter
                isAnimationActive={false}
                data={data.points as Array<{ week1: number; week4: number }>}
                name="Comptes"
                fill={COLORS.series1}
                fillOpacity={0.45}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}
